// Small global behaviours: sticky header shadow, search overlay focus, newsletter forms, click-to-load videos.
import { qs, qsa, on, t } from './dom.js';
import { toast } from './toast.js';
import { track } from './track.js';

export function initHeader() {
  const header = qs('[data-header]');
  if (!header) return;
  const update = () => {
    const s = window.scrollY > 20;
    header.classList.toggle('py-2', s);
    header.classList.toggle('shadow-md', s);
    header.classList.toggle('py-4', !s);
  };
  update();
  window.addEventListener('scroll', update, { passive: true });
}

export function initSearchOverlay() {
  document.addEventListener('kt:overlay:open', (e) => {
    if (e.detail.name === 'search') setTimeout(() => qs('[data-search-input]')?.focus(), 30);
  });
}

export function initNewsletter() {
  on(document, 'submit', '[data-newsletter]', async (e, form) => {
    e.preventDefault();
    const email = form.email.value.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast('error', t('common.email_invalid')); return; }
    const btn = form.querySelector('button[type=submit]');
    btn.disabled = true;
    try {
      // getAttribute: the form contains <input name="action"> (admin-post), which shadows the form.action property.
      const res = await fetch(form.getAttribute('action'), { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'fetch' } });
      const ok = res.ok;
      let msg = '';
      try { const j = await res.json(); msg = (j && j.data && j.data.message) || ''; } catch { msg = ''; }
      // Server messages are translated strings (kt_t); 'invalid'/'email' codes fall back to the generic text.
      toast(ok ? 'success' : 'error', ok ? (msg || t('home.newsletter_thanks')) : (msg && msg.length > 12 ? msg : t('common.email_invalid')));
      if (ok) { form.reset(); window.ktTrack?.('newsletter_signup', form.dataset.newsletter || ''); track('newsletter_signup', { method: form.dataset.newsletter || 'footer' }); } // analytics (1.9.0) + 1.9.8 tracking
    } catch {
      toast('error', 'Error');
    } finally {
      btn.disabled = false;
    }
  });
}

/** GDPR click-to-load: <div data-video="https://www.youtube-nocookie.com/embed/…|https://player.vimeo.com/video/…"><button data-video-load> (kt_video_player) */
export function initVideos() {
  on(document, 'click', '[data-video-load]', (e, btn) => {
    const box = btn.closest('[data-video]');
    if (!box) return;
    const iframe = document.createElement('iframe');
    iframe.src = box.dataset.video;
    iframe.className = 'absolute inset-0 w-full h-full';
    iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allowFullscreen = true;
    iframe.title = box.querySelector('.sr-only')?.textContent || 'Video';
    box.innerHTML = '';
    box.appendChild(iframe);
    window.ktTrack?.('video_play', /vimeo/.test(box.dataset.video) ? 'vimeo' : 'youtube'); // analytics (1.9.0)
    track('video_start', { video_provider: /vimeo/.test(box.dataset.video) ? 'vimeo' : 'youtube', video_title: iframe.title, video_url: box.dataset.video.split('?')[0] });
  });
  // 1.9.8 tracking: own MP4 files (Mediathek <video>): start + complete (embedded YouTube/Vimeo only report the start –
  // their player APIs would load third-party code)
  const seen = new WeakSet();
  const media = (e, name) => {
    const v = e.target;
    if (!(v instanceof HTMLVideoElement) || !v.controls) return;
    if (name === 'video_start') { if (seen.has(v)) return; seen.add(v); }
    const src = (v.currentSrc || '').split('?')[0];
    track(name, { video_provider: 'file', video_title: v.getAttribute('aria-label') || v.closest('[data-video-title]')?.dataset.videoTitle || document.title, video_url: src });
  };
  document.addEventListener('play', (e) => media(e, 'video_start'), true);
  document.addEventListener('ended', (e) => media(e, 'video_complete'), true);
}

/** Simple horizontal scroller buttons: [data-scroller] with [data-scroll="prev|next"] */
export function initScrollers() {
  on(document, 'click', '[data-scroll]', (e, btn) => {
    const sc = btn.closest('[data-scroller-wrap]')?.querySelector('[data-scroller]');
    if (!sc) return;
    sc.scrollBy({ left: (btn.dataset.scroll === 'next' ? 1 : -1) * sc.clientWidth * 0.8, behavior: 'smooth' });
  });
}

export function initRevealOnScroll() {
  const els = qsa('[data-reveal]');
  if (!els.length || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('animate-slide-up'); io.unobserve(en.target); } });
  }, { threshold: 0.1 });
  els.forEach((el) => io.observe(el));
}

/** Show/hide password: [data-password-field] > input + [data-password-toggle] (two icons, second one hidden). */
export function initPasswordToggle() {
  on(document, 'click', '[data-password-toggle]', (e, btn) => {
    const input = btn.closest('[data-password-field]')?.querySelector('input');
    if (!input) return;
    const show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    btn.setAttribute('aria-pressed', show ? 'true' : 'false');
    const icons = btn.querySelectorAll('svg');
    if (icons.length === 2) { icons[0].classList.toggle('hidden', show); icons[1].classList.toggle('hidden', !show); }
    input.focus();
  });
}

/** Server-rendered notices: <div data-server-toast data-type="success" data-message="…"> becomes a toast (box stays only without JS). */
export function initServerToasts() {
  qsa('[data-server-toast]').forEach((el) => {
    toast(el.dataset.type || 'info', el.dataset.message || el.textContent.trim());
    el.remove();
  });
}

/** Artists overview (/kuenstler/, KULTUR-202): instant client-side category filter on [data-artist-archive]. */
export function initArtistFilter() {
  const root = qs('[data-artist-archive]');
  if (!root) return;
  const pills = qsa('[data-artist-cat]', root);
  const cards = qsa('[data-artist-card]', root);
  const count = qs('[data-artist-count]', root);
  const empty = qs('[data-artist-empty]', root);
  const heading = qs('[data-artist-heading]', root);
  if (!pills.length || !cards.length) return;

  const apply = (cat, title, href) => {
    let n = 0;
    cards.forEach((c) => {
      const cats = (c.dataset.cats || '').split(/\s+/).filter(Boolean);
      const match = !cat || cats.includes(cat);
      c.hidden = !match;
      if (match) n++;
    });
    if (empty) empty.hidden = n > 0;
    if (count) count.textContent = (count.dataset.template || '{n}').replace('{n}', String(n));
    if (heading && title) heading.textContent = title;
    pills.forEach((p) => {
      const on = (p.dataset.artistCat || '') === cat;
      p.classList.toggle('pill-active', on);
      p.setAttribute('aria-pressed', String(on));
    });
    if (href && window.history?.replaceState) {
      window.history.replaceState(window.history.state, '', href);
    }
  };

  on(root, 'click', '[data-artist-cat]', (e, pill) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    apply(pill.dataset.artistCat || '', pill.dataset.catTitle || heading?.dataset.defaultTitle || '', pill.getAttribute('href') || '');
  });
}

